import moment from 'moment';

// Pure helpers for grouping the quotes nested inside a package, shared between the
// My Quotes table rendering (components/quotes/utils.tsx) and the quotes store slice.
// They MUST stay the single source of truth: the "Hotels (n)/Condos (n)/Cars (n)" badges
// and the package/standard tab classification both rely on the same group counts.

const getDateOnly = (value?: string | null): string => {
  if (!value) return '';
  return value.includes('T') ? value.split('T')[0] : value;
};

export const formatLocationName = (name: string) => {
  if (!name) return 'unknown';

  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
};

export const groupHotelByPackageDate = <T extends { checkIn?: string; checkOut?: string; location?: string; roomsDetail?: string; }>(data: T[]): {
  startDate: string;
  endDate: string;
  locationCode: string;
  rooms: any;
  items: T[];
}[] => {
  if (!Array.isArray(data)) return [];

  const grouped = data.reduce((acc: Record<string, T[]>, item) => {
    const start = item.checkIn ? getDateOnly(item.checkIn) : "unknown";
    const end = item.checkOut ? getDateOnly(item.checkOut) : "unknown";
    let locationCode = "unknown_location";
    try {
      const parsedLocation = item.location ? JSON.parse(item.location) : null;
      locationCode = parsedLocation?.code?.toString() || "unknown_location";
    } catch {
      locationCode = "unknown_location";
    }
    const roomsKey = item.roomsDetail
      ? item.roomsDetail.trim()
      : "no_rooms";
    const key = `${start}_${end}_${locationCode}_${roomsKey}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  return Object.entries(grouped).map(([key, items]) => {
    const [startDate, endDate, locationCode, roomsKey] = key.split("_");

    return {
      startDate,
      endDate,
      locationCode,
      rooms: roomsKey !== "no_rooms" ? roomsKey : [],
      items
    };
  });
};

export const groupByCondoDateRange = (data: any[]) => {
  if (!Array.isArray(data)) return [];
  const grouped = data.reduce((acc: any, item) => {
    const locationId = (item.locationObj?.id)?.toString() || item.place || "unknown_location";
    const roomsDetailStr = item.roomsDetail || item.occupancy || "";
    const monthsKey = (item.months || []).join(',');

    let key;
    if (item.searchType === "Flexible") {
      key = monthsKey ? `Flexible_${monthsKey}_${roomsDetailStr}_${locationId}` : `Flexible_Anytime_${roomsDetailStr}_${locationId}`;
    } else {
      key = `${item.checkIn}_${item.checkOut}_${roomsDetailStr}_${locationId}`;
    }

    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  return Object.entries(grouped).map(([_, items]: [string, any]) => ({
    startDate: items[0].checkIn || '',
    endDate: items[0].checkOut || '',
    searchType: items[0].searchType,
    selectedMonths: items[0].months || [],
    roomsDetail: items[0].roomsDetail || items[0].occupancy || '',
    items,
  }));
};

export const groupByCarDateRange = (data: any[]) => {
  if (!Array.isArray(data)) return [];
  const grouped = data.reduce((acc: any, item) => {
    const start = item.checkIn || "unknown";
    const end = item.checkOut || "unknown";

    const pDate = item.pickUpDate ? moment(item.pickUpDate) : null;
    const dDate = item.dropOffDate ? moment(item.dropOffDate) : null;
    const pickUpTime = pDate?.isValid() ? pDate.format('HH:mm') : (item.carRequest?.pickUpTime || "unknown");
    const returnTime = dDate?.isValid() ? dDate.format('HH:mm') : (item.carRequest?.returnTime || "unknown");
    const countryISO = item.countryISO || item.carRequest?.countryISO || "unknown";
    const driverAge = item.driverAge ?? item.carRequest?.driverAge ?? "unknown";

    const pickUpLocationCode = formatLocationName(item.pickUpLocationObj?.code?.toString() || item.pickUpLocationDetail) || "unknown_location";
    const dropOffLocationCode = formatLocationName((item.dropOffLocationObj?.code || item.pickUpLocationObj?.code)?.toString() || item.pickUpLocationDetail) || "unknown_location";
    const key = `${start}_${end}_${pickUpTime}_${returnTime}_${countryISO}_${driverAge}_${pickUpLocationCode}_${dropOffLocationCode}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  return Object.entries(grouped).map(([_, items]: [string, any]) => {
    const first = items[0];
    const pDate = first.pickUpDate ? moment(first.pickUpDate) : null;
    const dDate = first.dropOffDate ? moment(first.dropOffDate) : null;
    return {
      startDate: first.checkIn || first.startDate,
      endDate: first.checkOut || first.endDate,
      pickUpTime: pDate?.isValid() ? pDate.format('HH:mm') : first.carRequest?.pickUpTime,
      returnTime: dDate?.isValid() ? dDate.format('HH:mm') : first.carRequest?.returnTime,
      countryISO: first.countryISO || first.carRequest?.countryISO,
      driverAge: first.driverAge ?? first.carRequest?.driverAge,
      items
    };
  });
};

interface IPackageLike {
  packageQuotes?: {
    hotelQuotes?: any[];
    condoQuotes?: any[];
    carQuotes?: any[];
  };
}

export const getDistinctQuoteIds = (items: any[] = []): string[] =>
  Array.from(new Set((items || []).map((q) => q?.quoteBaseId).filter(Boolean)));

// Number of product categories (hotels / condos / cars) that contain at least one quote.
export const getNonEmptyPackageCategories = (d: IPackageLike): number => {
  const pq = d?.packageQuotes;
  if (!pq) return 0;
  return [pq.hotelQuotes, pq.condoQuotes, pq.carQuotes].filter((arr) => !!arr && arr.length > 0).length;
};

// A package wrapper represents a real package only when it groups more than one quote:
// quotes from more than one category (e.g. a hotel + a condo + a car), or more than one
// distinct quoteBaseId. A wrapper holding a single quote — one category and one
// quoteBaseId, no matter how many comparison options/locations it carries — is just one
// quote and belongs in its own tab, not the Package tab.
export const isMultiQuotePackage = (d: IPackageLike): boolean => {
  const pq = d?.packageQuotes;
  if (!pq) return false;
  const allNested = [...(pq.hotelQuotes || []), ...(pq.condoQuotes || []), ...(pq.carQuotes || [])];
  return getNonEmptyPackageCategories(d) > 1 || getDistinctQuoteIds(allNested).length > 1;
};

// A quote's relevant date (hotel/condo check-in, car pick-up) is in the past when it is
// before today.
export const isQuoteDatePast = (date?: string): boolean => !!date && moment(date).isBefore(moment(), 'day');

// Whether a single quote is still bookable — its date is today or in the future. A condo is
// bookable when at least one of its available check-in dates is still valid. This is the
// same notion that enables/disables the "view and reserve" button.
export const isQuoteValid = (q: any): boolean => {
  if (!q) return false;
  if (q.quoteType === 'Car') return !isQuoteDatePast(q.pickUpDate);
  if (q.isCondo) {
    const dates = q.availableDates;
    return dates?.length ? dates.some((d: any) => !isQuoteDatePast(d?.from)) : !isQuoteDatePast(q.checkIn);
  }
  return !isQuoteDatePast(q.checkIn);
};

// All quotes nested in a package, flattened across categories.
export const getAllPackageQuotes = (d: IPackageLike): any[] => {
  const pq = d?.packageQuotes;
  if (!pq) return [];
  return [...(pq.hotelQuotes || []), ...(pq.condoQuotes || []), ...(pq.carQuotes || [])];
};

// Whether a package (or any list of quotes) still has at least one bookable quote.
export const hasAnyValidQuote = (quotes: any[]): boolean => (quotes || []).some(isQuoteValid);
